import { useState, useEffect, useCallback, memo } from 'react';
import { useMapStore } from '@/stores/mapStore';

const DISMISS_KEY = 'tensormaps-location-prompt-dismissed';

export const LocationPermissionPrompt = memo(function LocationPermissionPrompt() {
  const {
    locationPermission,
    setLocationPermission,
    setUserLocationState,
    setLocationError,
  } = useMapStore();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  // Check if user previously dismissed the prompt
  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Reset dismissed state if permission changes to granted
  useEffect(() => {
    if (locationPermission === 'granted') {
      localStorage.removeItem(DISMISS_KEY);
      setIsDismissed(false);
    }
  }, [locationPermission]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    localStorage.setItem(DISMISS_KEY, 'true');
  }, []);

  // Directly request browser/device location permission
  const handleRequestPermission = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setIsRequesting(true);

    // This triggers the browser's native permission dialog
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Permission granted - update state with location
        setIsRequesting(false);
        setLocationPermission('granted');
        setUserLocationState({
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude ?? undefined,
          altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
          heading: position.coords.heading ?? undefined,
          speed: position.coords.speed ?? undefined,
          timestamp: position.timestamp,
          source: position.coords.accuracy < 100 ? 'gps' : 'network',
        });
        setLocationError(null);
      },
      (error) => {
        setIsRequesting(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          setLocationError('Location permission denied');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setLocationError('Location unavailable. Please enable GPS/location services.');
        } else if (error.code === error.TIMEOUT) {
          setLocationError('Location request timed out. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [setLocationPermission, setUserLocationState, setLocationError]);

  // Don't show if dismissed, permission granted, or unavailable
  if (isDismissed || locationPermission === 'granted' || locationPermission === 'unavailable') {
    return null;
  }

  // Only show on 'prompt' state (before user has made a decision)
  // Don't show if already denied - browser won't show dialog again
  if (locationPermission !== 'prompt') {
    return null;
  }

  return (
    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-overlay max-w-md w-full px-4 animate-slide-down">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 flex items-center gap-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <LocationIcon className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">Enable location services</p>
            <p className="text-xs text-gray-500 mt-0.5">
              For navigation and nearby places
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDismiss}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
            >
              Not now
            </button>
            <button
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRequesting ? (
                <span className="flex items-center gap-1">
                  <LoadingSpinner className="w-3 h-3" />
                  Requesting...
                </span>
              ) : (
                'Allow'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Icons
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}
